import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'

import { ApiError, isApiError } from '@/api'
import { BulkInviteRequest, ContactAdminRequest, InviteRequest } from '@/api/generated'
import {
  bulkInviteUsers,
  contactAdmin,
  inviteSelf,
  inviteUser,
  listInvites,
  listMembers,
  removeMember,
  RESEND_INVITATIONS_MAX_IDS,
  ResendInvitationResult,
  resendInvitations,
  revokeInvite,
  updateMemberRole,
} from '@/api/members'
import { RootState } from '@/store'
import { getEnterpriseThunk } from '@/store/features/user/userSlice'
import { Invite, Member, Role } from '@/types'
import { captureEvent } from '@/utils/captureEvent'
import { EventName, PLATFORM_ADMIN_PORTAL } from '@/utils/eventDefinitions'
import { bucketMembers } from '@/utils/seatBreakdown'
import { sortInvites, sortMembers } from '@/utils/sorting'

interface MemberState {
  members: Member[]
  invites: Invite[]
  isLoading: boolean
  hasLoaded: boolean
  resendingInviteIds: Record<string, true>
}

const initialState: MemberState = {
  members: [],
  invites: [],
  isLoading: false,
  hasLoaded: false,
  resendingInviteIds: {},
}

export const fetchAllUsersThunk = createAsyncThunk('member/fetchAll', async (_, { rejectWithValue }) => {
  try {
    // TODO: Only try to fetch invites if you are an admin
    const [membersResult, invitesResult] = await Promise.allSettled([listMembers(), listInvites()])

    // Handle members result - reject if members fail (critical data)
    if (membersResult.status === 'rejected') {
      console.error('Failed to fetch members:', membersResult.reason)
      return rejectWithValue('Failed to fetch members')
    }
    const members = membersResult.value.data.members

    // Handle invites result - gracefully handle errors (invitations are not critical)
    const invites = invitesResult.status === 'fulfilled' ? invitesResult.value.data.invitations : []

    return { members, invites }
  } catch (error) {
    const message = 'Failed to fetch members & invites'
    console.error(message, error)
    return rejectWithValue(message)
  }
})

export const inviteUserThunk = createAsyncThunk(
  'member/inviteUser',
  async (request: InviteRequest, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await inviteUser(request)
      captureEvent(EventName.InviteSent, {
        role: request.role ?? '',
        success: true,
        source: 'invite_user',
        platform: PLATFORM_ADMIN_PORTAL,
      })
      dispatch(getEnterpriseThunk({ needSubscription: false }))
      return data
    } catch (error) {
      let message = 'Failed to invite users'
      if ((error as ApiError).detail == 'invitation_exists') {
        message = 'User already invited'
      } else if ((error as ApiError).detail == 'no_subscription') {
        message = 'No subscription found'
      }
      captureEvent(EventName.InviteSent, {
        role: request.role ?? '',
        success: false,
        source: 'invite_user',
        platform: PLATFORM_ADMIN_PORTAL,
      })
      console.error(message, error)
      return rejectWithValue(message)
    }
  }
)

export const bulkInviteUsersThunk = createAsyncThunk(
  'member/bulkInviteUsers',
  async (request: BulkInviteRequest, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await bulkInviteUsers(request)
      const roleBreakdown: Record<string, number> = {}
      for (const invite of request.invites) {
        const role = invite.role ?? ''
        roleBreakdown[role] = (roleBreakdown[role] || 0) + 1
      }
      captureEvent(EventName.BulkInviteSent, {
        count: request.invites.length,
        role_breakdown: roleBreakdown,
        success: true,
        source: 'bulk_invite',
        platform: PLATFORM_ADMIN_PORTAL,
      })
      dispatch(getEnterpriseThunk({ needSubscription: false }))
      return data
    } catch (error) {
      let message = (error as ApiError).message || 'Failed to invite users'
      if ((error as ApiError).detail == 'no_subscription') {
        message = 'No subscription found'
      }
      captureEvent(EventName.BulkInviteSent, {
        count: request.invites.length,
        role_breakdown: {},
        success: false,
        source: 'bulk_invite',
        platform: PLATFORM_ADMIN_PORTAL,
      })
      console.error(message, error)
      return rejectWithValue(message)
    }
  }
)

export const inviteSelfThunk = createAsyncThunk(
  'member/inviteSelf',
  async (request: ContactAdminRequest, { rejectWithValue }) => {
    try {
      const { data } = await inviteSelf(request)
      captureEvent(EventName.TeamSelfInviteSent, {
        success: true,
        source: 'self_invite',
        platform: PLATFORM_ADMIN_PORTAL,
      })
      return data
    } catch (error) {
      captureEvent(EventName.TeamSelfInviteSent, {
        success: false,
        source: 'self_invite',
        platform: PLATFORM_ADMIN_PORTAL,
      })
      const message = 'Failed to contact admins, please try again later or contact support'
      console.error(message, error)
      return rejectWithValue(message)
    }
  }
)

export const contactAdminThunk = createAsyncThunk(
  'member/contactAdmin',
  async (request: ContactAdminRequest, { rejectWithValue }) => {
    try {
      const { data } = await contactAdmin(request)
      captureEvent(EventName.TeamInviteRequestSent, {
        success: true,
        source: 'contact_admin',
        platform: PLATFORM_ADMIN_PORTAL,
      })
      return data
    } catch (error) {
      captureEvent(EventName.TeamInviteRequestSent, {
        success: false,
        source: 'contact_admin',
        platform: PLATFORM_ADMIN_PORTAL,
      })
      const message = 'Failed to contact admins, please try again later or contact support'
      console.error(message, error)
      return rejectWithValue(message)
    }
  }
)

export const updateMemberRoleThunk = createAsyncThunk(
  'member/updateMemberRole',
  async ({ userId, role }: { userId: string; role: Role }, { rejectWithValue }) => {
    try {
      const { data } = await updateMemberRole(userId, { role })
      captureEvent(EventName.RoleChanged, { user_id: userId, new_role: role })
      return data
    } catch (error) {
      let message = 'Failed to update user'
      if (isApiError(error) && error.status === 409 && error.detail) {
        message = error.detail
      }
      console.error(message, error)
      return rejectWithValue(message)
    }
  }
)

export const revokeInviteThunk = createAsyncThunk(
  'member/deleteInvite',
  async ({ email }: { email: string }, { dispatch, rejectWithValue }) => {
    try {
      await revokeInvite(email)
      captureEvent(EventName.InviteRevoked, { email })
      dispatch(getEnterpriseThunk({ needSubscription: false }))
      return email
    } catch (error) {
      const message = 'Failed to delete invite'
      console.error(message, error)
      return rejectWithValue(message)
    }
  }
)

export const removeMemberThunk = createAsyncThunk(
  'member/removeMember',
  async ({ userId }: { userId: string }, { dispatch, rejectWithValue }) => {
    try {
      await removeMember(userId)
      captureEvent(EventName.MemberRemoved, { user_id: userId })
      dispatch(getEnterpriseThunk({ needSubscription: false }))
      return userId
    } catch (error) {
      // Any status can carry an explanation worth showing, e.g. the 403 telling a SCIM
      // enterprise that membership is controlled by their IdP.
      let message = 'Failed to remove user'
      if (isApiError(error) && error.detail) {
        message = error.detail
      }
      console.error(message, error)
      return rejectWithValue(message)
    }
  }
)

/**
 * Chunks are sent one after another so a large org can't fan out past the server's rate limit. If a
 * later chunk fails, the earlier ones were already emailed, so their results are kept and the unsent
 * ids come back as `failed` rather than rejecting the whole call.
 */
export const resendInvitationsThunk = createAsyncThunk(
  'member/resendInvitations',
  async ({ invitationIds }: { invitationIds: string[] }, { rejectWithValue }) => {
    const results: ResendInvitationResult[] = []
    for (let start = 0; start < invitationIds.length; start += RESEND_INVITATIONS_MAX_IDS) {
      try {
        const { data } = await resendInvitations({
          invitation_ids: invitationIds.slice(start, start + RESEND_INVITATIONS_MAX_IDS),
        })
        results.push(...data.results)
      } catch (error) {
        console.error('Failed to resend invites', isApiError(error) ? error.status : 'unknown')
        if (results.length === 0) {
          return rejectWithValue(isApiError(error) ? error.message : "Couldn't resend invites")
        }
        results.push(
          ...invitationIds.slice(start).map((id) => ({
            invitation_id: id,
            outcome: 'failed' as const,
            retry_after_seconds: null,
          }))
        )
        break
      }
    }
    return results
  }
)

const memberSlice = createSlice({
  name: 'member',
  initialState,
  reducers: {
    clearMembers: (state) => {
      state.members = []
      state.invites = []
      state.hasLoaded = false
      state.resendingInviteIds = {}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllUsersThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.members = action.payload.members as Member[]
        state.invites = action.payload.invites
        state.hasLoaded = true
      })
      .addCase(fetchAllUsersThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchAllUsersThunk.rejected, (state) => {
        state.isLoading = false
      })

    builder
      .addCase(inviteUserThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(inviteUserThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.invites.push(action.payload)
      })
      .addCase(inviteUserThunk.rejected, (state) => {
        state.isLoading = false
      })

    builder
      .addCase(bulkInviteUsersThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(bulkInviteUsersThunk.fulfilled, (state, action) => {
        state.isLoading = false
        // Add all successful invites to state
        state.invites.push(...action.payload.successful)
      })
      .addCase(bulkInviteUsersThunk.rejected, (state) => {
        state.isLoading = false
      })

    builder
      .addCase(revokeInviteThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(revokeInviteThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.invites = state.invites.filter((invite) => invite.email !== action.payload)
      })
      .addCase(revokeInviteThunk.rejected, (state) => {
        state.isLoading = false
      })

    builder
      .addCase(resendInvitationsThunk.pending, (state, action) => {
        for (const id of action.meta.arg.invitationIds) state.resendingInviteIds[id] = true
      })
      .addCase(resendInvitationsThunk.fulfilled, (state, action) => {
        for (const id of action.meta.arg.invitationIds) delete state.resendingInviteIds[id]
      })
      .addCase(resendInvitationsThunk.rejected, (state, action) => {
        for (const id of action.meta.arg.invitationIds) delete state.resendingInviteIds[id]
      })

    builder
      .addCase(removeMemberThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(removeMemberThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.members = state.members.filter((member) => member.userId !== action.payload)
      })
      .addCase(removeMemberThunk.rejected, (state) => {
        state.isLoading = false
      })

    builder
      .addCase(updateMemberRoleThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(updateMemberRoleThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.members = state.members.map((member) =>
          member.userId === action.payload.userId ? (action.payload as Member) : member
        )
      })
      .addCase(updateMemberRoleThunk.rejected, (state) => {
        state.isLoading = false
      })
  },
})

// Export actions
export const { clearMembers } = memberSlice.actions

// Export selectors
export const selectMembers = (state: RootState) => state.member.members
export const selectInvites = (state: RootState) => state.member.invites
export const selectIsMembersLoading = (state: RootState) => state.member.isLoading
export const selectResendingInviteIds = (state: RootState) => state.member.resendingInviteIds

// Memoized sorted selectors - only recalculate when data changes
export const selectSortedMembers = createSelector([selectMembers], (members) => sortMembers(members))

export const selectSortedInvites = createSelector([selectInvites], (invites) => sortInvites(invites))

// Seat-breakdown selectors. Bucketing logic lives in `@/utils/seatBreakdown`;
// see that file for invariants and the trialing-IT-admin edge case.
const selectMemberBuckets = createSelector([selectMembers], bucketMembers)
export const selectPaidSeats = (state: RootState) => selectMemberBuckets(state).paid
export const selectTrialingMembers = (state: RootState) => selectMemberBuckets(state).trialing
export const selectItAdmins = (state: RootState) => selectMemberBuckets(state).itAdmin
export const selectTotalPendingInvites = (state: RootState) => state.member.invites.length
export const selectTotalSeatsUsed = selectPaidSeats

// Export reducer
export default memberSlice.reducer
